import type {ChangeEvent} from 'react';

import {useThemeColors} from '@nfq/colors';

import {useFormField} from 'UI/components/action/inputs/hooks/useFormField';

import type {ValidationFunc} from 'Shared/formHelperTypes';

/**
 * Custom hook for managing text input state, validation, and theme colors in forms.
 * This hook combines form field management with text input specific functionality, providing
 * a complete solution for handling text input components with validation and change events.
 *
 * @param name         The name identifier for the form field used for tracking and validation.
 * @param initialValue The initial value to set for the text input field.
 * @param validation   Optional validation function to validate the input value.
 * @param onChange     Callback function that handles the input change event.
 * @returns Object containing colors, error state, field reference, change handler, and current value.
 *
 * @example
 * ```tsx
 * const {colors, error, fieldRef, handleChange, value} = useTextInput(
 *     'username',
 *     '',
 *     (value) => value.length > 0 ? null : 'Username is required',
 *     (e) => console.log('Input changed:', e.target.value)
 * );
 * ```
 */
export const useTextInput = (
    name: string,
    initialValue: string,
    validation: (ValidationFunc | undefined),
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
) => {
    const colors = useThemeColors();
    const {
        error,
        fieldRef,
        formId,
        handleFieldChange,
        value
    } = useFormField<string, HTMLInputElement>(name, initialValue, initialValue, false, validation);

    /**
     * Handles input change events by extracting the target value and calling both external and internal change handlers.
     * This function serves as a bridge between the external onChange callback and the internal form field management,
     * ensuring that both the parent component and the form field state are properly updated when the input value changes.
     *
     * @param e The change event from the HTML input element containing the new value.
     */
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const {value: targetValue} = e.target;

        onChange(e);

        handleFieldChange(targetValue);
    };

    return {
        colors,
        error,
        fieldRef,
        formId,
        handleChange,
        value
    };
};